import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SearchCard } from "../components/SearchCard";
import { SectionTitle, EmptyState, Loader, StyleTag } from "../components/ui-bits";
import { STYLES, BUDGETS, MOODS } from "../lib/constants";
import { Search, SlidersHorizontal, X, Sparkles } from "lucide-react";

export default function BeatmakerFeed() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [reco, setReco] = useState([]);
  const [filters, setFilters] = useState({ style: "", budget: "", mood: "", sort: "recent" });
  const [showFilters, setShowFilters] = useState(false);

  const load = () => {
    setItems(null);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/searches", { params }).then((r) => setItems(r.data));
  };
  useEffect(load, [filters]);
  useEffect(() => { api.get("/searches/recommended").then((r) => setReco(r.data)).catch(() => {}); }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: f[k] === v ? "" : v }));
  const clear = () => setFilters({ style: "", budget: "", mood: "", sort: "recent" });
  const activeCount = [filters.style, filters.budget, filters.mood].filter(Boolean).length;

  const noStyles = (user?.styles || []).length === 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <SectionTitle sub="Trouve des artistes qui cherchent ton style.">Recherches d'artistes</SectionTitle>
        <button onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters-button"
          className="relative shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-700 hover:border-violet-600 text-sm font-medium transition-colors -mt-4">
          <SlidersHorizontal size={16} /> Filtres
          {activeCount > 0 && <span className="w-5 h-5 rounded-full bg-violet-500 text-[10px] font-bold flex items-center justify-center">{activeCount}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="bg-[#0B0716] border border-zinc-800 rounded-2xl p-4 mb-6 space-y-4 animate-fade-up">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Style</p>
            <div className="flex flex-wrap gap-2">{STYLES.map((s) => <StyleTag key={s} active={filters.style === s} onClick={() => setF("style", s)}>{s}</StyleTag>)}</div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Budget</p>
            <div className="flex flex-wrap gap-2">{BUDGETS.map((b) => <StyleTag key={b} active={filters.budget === b} onClick={() => setF("budget", b)}>{b}</StyleTag>)}</div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Ambiance</p>
            <div className="flex flex-wrap gap-2">{MOODS.map((m) => <StyleTag key={m} active={filters.mood === m} onClick={() => setF("mood", m)}>{m}</StyleTag>)}</div>
          </div>
          {activeCount > 0 && <button onClick={clear} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white"><X size={14} /> Réinitialiser</button>}
        </div>
      )}

      {activeCount === 0 && (
        reco.length > 0 ? (
          <div className="mb-9">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-violet-400" />
              <p className="text-sm font-semibold">Recommandé pour toi</p>
              <span className="text-xs text-zinc-500">· basé sur tes styles</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reco.slice(0, 6).map((s) => <SearchCard key={s.id} search={s} matchedStyles={s.matched_styles} testIdPrefix="reco-search-card" />)}
            </div>
            <div className="h-px bg-zinc-800 mt-8" />
          </div>
        ) : noStyles ? (
          <div className="mb-8 flex items-center gap-3 p-4 rounded-2xl bg-violet-600/10 border border-violet-600/25">
            <Sparkles size={18} className="text-violet-400 shrink-0" />
            <p className="text-sm text-violet-200">Ajoute tes styles musicaux dans ton profil pour recevoir des recommandations personnalisées.</p>
          </div>
        ) : null
      )}

      {activeCount === 0 && reco.length > 0 && <p className="text-sm font-semibold mb-3">Toutes les recherches</p>}

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Search} title="Aucune recherche" desc="Aucune recherche ne correspond à ces filtres pour le moment." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => <SearchCard key={s.id} search={s} />)}
        </div>
      )}
    </div>
  );
}
